import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { Session } from "@supabase/supabase-js";
import { bootstrapSession } from "../lib/api";
import { supabase } from "../lib/supabase";
import type { PermissionRow, UserProfile, WorkContext } from "../types";

interface SessionState {
  session: Session | null;
  profile: UserProfile | null;
  permissions: PermissionRow[];
  context: WorkContext | null;
  loading: boolean;
  refresh: () => Promise<void>;
  selectContext: (context: WorkContext) => void;
  clearContext: () => void;
}

const SessionContext = createContext<SessionState | null>(null);
const contextKey = "kalite.work-context";

export function SessionProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [permissions, setPermissions] = useState<PermissionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [context, setContext] = useState<WorkContext | null>(() => {
    try {
      return JSON.parse(localStorage.getItem(contextKey) || "null") as WorkContext | null;
    } catch {
      return null;
    }
  });

  const refresh = useCallback(async () => {
    const { data } = await supabase.auth.getSession();
    setSession(data.session);
    if (!data.session) {
      setProfile(null);
      setPermissions([]);
      setLoading(false);
      return;
    }
    const result = await bootstrapSession();
    setProfile(result.profile);
    setPermissions(result.permissions.filter((item) => item.is_active !== false));
    setLoading(false);
  }, []);

  useEffect(() => {
    void refresh();
    const { data } = supabase.auth.onAuthStateChange((_event, nextSession) => {
      setSession(nextSession);
      if (!nextSession) {
        setProfile(null);
        setPermissions([]);
        setContext(null);
        localStorage.removeItem(contextKey);
      } else {
        queueMicrotask(() => void refresh());
      }
    });
    return () => data.subscription.unsubscribe();
  }, [refresh]);

  const selectContext = useCallback((value: WorkContext) => {
    setContext(value);
    localStorage.setItem(contextKey, JSON.stringify(value));
  }, []);

  const clearContext = useCallback(() => {
    setContext(null);
    localStorage.removeItem(contextKey);
  }, []);

  const value = useMemo(
    () => ({ session, profile, permissions, context, loading, refresh, selectContext, clearContext }),
    [session, profile, permissions, context, loading, refresh, selectContext, clearContext],
  );
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession() {
  const value = useContext(SessionContext);
  if (!value) throw new Error("useSession debe usarse dentro de SessionProvider.");
  return value;
}
